import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import CourseEditModal from "../TabComponents/CourseEditModal";
import { useNavigate } from "react-router";
import { fetchCourses, addCourse, updateCourse, deleteCourse } from "../../../../../utils/Apis";
import { safeTruncate } from "../../../../../utils/truncate";

const CoursesTab = () => {
  const dispatch = useDispatch();
  const courses = useSelector((state) => state.data.courses?.courses || []);
  const loading = useSelector((state) => state.data.loading);
  const error = useSelector((state) => state.data.error);

  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState("add");
  const [selectedCourseId, setSelectedCourseId] = useState();
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    courseId: null,
    courseName: ""
  });

  const navigate = useNavigate();

  // ✅ Fetch courses when component mounts
  useEffect(() => {
    if (!courses.length > 0){
    dispatch(fetchCourses())
    }
  }, []);

  const handleAddClick = () => {
    setEditMode("add");
    setSelectedCourseId(null);
    setOpenModal(true);
  };

  const handleEditClick = (courseId) => {
    setEditMode("edit");
    setSelectedCourseId(courseId);
    setOpenModal(true);
  };

  const handleDeleteClick = (courseId, courseName) => {
    setDeleteDialog({
      open: true,
      courseId,
      courseName
    });
  };

  const handleConfirmDelete = () => {
    dispatch(deleteCourse(deleteDialog.courseId))
      .unwrap()
      .then(() => {
        setNotification({
          open: true,
          message: "Course deleted successfully",
          severity: "success",
        });
      })
      .catch((errorMsg) => {
        setNotification({
          open: true,
          message: `Failed to delete course: ${errorMsg}`,
          severity: "error",
        });
      });
  
    // Close the dialog
    setDeleteDialog({ open: false, courseId: null, courseName: "" });
  };  

  const handleCancelDelete = () => {
    setDeleteDialog({ open: false, courseId: null, courseName: "" });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedCourseId(null);
  };
  const handleSubmitCourse = (submissionData, file) => {
    let formData = new FormData()
    const {coursePic, id, ...restOfData} = submissionData
    formData.append('courseData', JSON.stringify(submissionData))
    if(id) formData.append('id', id)
    formData.append('uploaded_file', coursePic)
    if (editMode === "edit") {
      dispatch(updateCourse(formData))
        .unwrap()
        .then(() => {
          setNotification({
            open: true,
            message: "Course updated successfully",
            severity: "success",
          });
        })
        .catch((errorMsg) => {
          setNotification({
            open: true,
            message: `Failed to update course: ${errorMsg}`,
            severity: "error",
          });
        });
    } else {
      dispatch(addCourse(formData))
        .unwrap()
        .then(() => {
          setNotification({
            open: true,
            message: "Course added successfully",
            severity: "success",
          });
        })
        .catch((errorMsg) => {
          setNotification({
            open: true,
            message: `Failed to add course: ${errorMsg}`,
            severity: "error",
          });
        });
    }
  };
  

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" fontWeight="bold">
          Courses
        </Typography>
        <Button variant="contained" color="primary" onClick={handleAddClick}>
          Add New Course
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {courses.length === 0 ? (
            <Grid item xs={12}>
              <Typography align="center" p={4}>
                No courses available. Click "Add New Course" to create one.
              </Typography>
            </Grid>
          ) : (
            courses.map((course) => (
              <Grid item xs={12} sm={6} md={4} key={course._id}>
                <Card
                  sx={{
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 16px 30px rgba(0, 0, 0, 0.15)"
                    },
                    borderRadius: "16px",
                    overflow: "hidden",
                    maxWidth:"25rem",
                    minWidth:"18rem",
                  }}
                >
                  <Box sx={{ position: "relative", height: "200px" }}>
                    <img
                      src={course.coursePic || ""}
                      alt={course.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover"
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "";
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        width: "100%",
                        height: "40%",
                        display: "flex",
                        gap: 2,
                        background:
                          "linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent)",
                        color: "white",
                        p: 2,
                        alignItems: "flex-end"
                      }}
                    >
                      <Typography variant="subtitle2">
                        {course.duration.value} {course.duration.unit}
                      </Typography>
                      {course.feeStructure && (
                        <Typography variant="subtitle2">
                          ₹{course.feeStructure.finalFee}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <CardContent sx={{ textAlign: "center", p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        mb: 1,
                        fontFamily: "Roboto, sans-serif"
                      }}
                    >
                      {course.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#7F8C8D",
                        mb: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical"
                      }}
                    >
                      {safeTruncate(course.description, 80)}
                    </Typography>
                    <Box
                      sx={{
                        justifyContent: "space-evenly",
                        display: "flex",
                        flexDirection: "row",
                        gap:2
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="secondary"
                        sx={{
                          "&:hover": {
                            backgroundColor: "#3246758a"
                          },
                          padding: "8px 20px",
                          borderRadius: "24px"
                        }}
                        onClick={() => handleEditClick(course._id)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        sx={{
                          "&:hover": {
                            backgroundColor: "#7532328a"
                          },
                          padding: "8px 20px",
                          borderRadius: "24px"
                        }}
                        onClick={() =>
                          handleDeleteClick(course._id, course.name)
                        }
                      >
                        Delete
                      </Button>
                      <Button
                        variant="contained"
                        sx={{
                          backgroundColor: "#333",
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: "black"
                          },
                          padding: "8px 20px",
                          borderRadius: "24px",
                          textTransform: "none"
                        }}
                        onClick={() => navigate(`/admission`)}
                      >
                        Enroll Now
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Course Edit Modal */}
      <CourseEditModal
        open={openModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmitCourse}
        mode={editMode}
        courseid={selectedCourseId}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the course "
            {deleteDialog.courseName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CoursesTab;